/* eslint-disable max-lines */
import { atom, useAtom } from 'jotai';

import { fireFireworks } from '@/helpers/confetti';
import {
  trackGettingStartedCompleted,
  trackGettingStartedPanelClosed,
  trackGettingStartedPanelNextStep,
  trackGettingStartedPanelOpened,
} from '@/lib/analytics/scribe/getting-started';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  ConsultNoteStatus,
  CreateDocumentPages,
  CreateTemplatePages,
  NotePages,
  OnboardingTask,
  SessionPages,
  TemplatePages,
  TutorialSteps,
} from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutateUpdateUserOnboardingTask } from './mutation/useMutateUpdateUserOnboardingTask';
import { useMutateUpdateUserOnboardingSubTask } from './mutation/useMutateUserOnboardingSubTask';
import { useQueryUser } from './query/useQueryUser';

// Tutorial videos are served from public/videos/tutorial/ (see public/videos/),
// so the bundler resolves them as plain public URLs with no file/asset loader.
const TemplateAddToYourLibrary = '/videos/tutorial/template/add-to-library.mp4';
const TemplateBrowseTemplates = '/videos/tutorial/template/browse-community.mp4';
const TemplateGoToTemplateCommunity = '/videos/tutorial/template/go-to-community.mp4';
const TemplateMoveBackToSession = '/videos/tutorial/template/navigate-back.mp4';
const SessionSaveContext = '/videos/tutorial/session/add-context.mp4';
const SessionCheckMicrophone = '/videos/tutorial/session/check-mic.mp4';
const SessionStart = '/videos/tutorial/session/create-session.mp4';
const SessionSetTemplate = '/videos/tutorial/session/set-template.mp4';
const SessionStartTranscribing = '/videos/tutorial/session/start-transcribing.mp4';
const SessionStopTranscribing = '/videos/tutorial/session/stop-transcribing.mp4';
const NoteHelpHeidi = '/videos/tutorial/note/note-1.mp4';
const NoteAskHeidi = '/videos/tutorial/note/note-2.mp4';
const DocumentCreate = '/videos/tutorial/create-document/create-doc-1.mp4';
const DocumentSelect = '/videos/tutorial/create-document/create-doc-2.mp4';
const CreateTemplateOpenModal = '/videos/tutorial/create-template/create-template-1.mp4';
const CreateTemplatePromptHeidi =
  '/videos/tutorial/create-template/create-template-2.mp4';
const CreateTemplateSave = '/videos/tutorial/create-template/create-template-3.mp4';

export type Page =
  | TemplatePages
  | SessionPages
  | CreateTemplatePages
  | NotePages
  | CreateDocumentPages;

const defaultCompletedSteps = [TutorialSteps.SIGN_UP, TutorialSteps.ORGANISATION];

export const panelOpenAtom = atom(false);
export const tutorialVisibleAtom = atom(true); // Controls visibility of entire tutorial component
export const tutorialStepAtom = atom<{ step: TutorialSteps; page: Page } | undefined>();
export const completedStepsAtom = atom<TutorialSteps[]>(defaultCompletedSteps);

export const stepsMapping = {
  [TutorialSteps.SIGN_UP]: {
    title: 'tutorial.signUp',
    steps: [],
  },
  [TutorialSteps.ORGANISATION]: {
    title: 'tutorial.provideOrganisation',
    steps: [],
  },
  [TutorialSteps.SESSION]: {
    title: 'tutorial.startSession',
    steps: [
      {
        title: 'tutorial.createNewSession',
        description: 'tutorial.createNewSessionDesc',
        image: SessionStart,
        enum: SessionPages.START_SESSION,
        order: 1,
        total: 6,
      },
      {
        title: 'tutorial.checkMicrophone',
        description: 'tutorial.checkMicrophoneDesc',
        image: SessionCheckMicrophone,
        enum: SessionPages.CHECK_MICROPHONE,
        order: 2,
        total: 6,
      },
      {
        title: 'tutorial.runConsult',
        description: 'tutorial.runConsultDesc',
        enum: SessionPages.RUN_CONSULT,
        image: SessionStartTranscribing,
        order: 3,
        total: 6,
      },
      {
        title: 'tutorial.chooseTemplate',
        description: 'tutorial.chooseTemplateDesc',
        enum: SessionPages.CHOOSE_TEMPLATE,
        image: SessionSetTemplate,
        order: 4,
        total: 6,
      },
      {
        title: 'tutorial.addContext',
        description: 'tutorial.addContextDesc',
        enum: SessionPages.ADD_CONTEXT,
        image: SessionSaveContext,
        order: 5,
        total: 6,
      },
      {
        title: 'tutorial.stopSession',
        description: 'tutorial.stopSessionDesc',
        enum: SessionPages.STOP_SESSION,
        image: SessionStopTranscribing,
        order: 6,
        total: 6,
      },
    ],
  },
  [TutorialSteps.NOTE]: {
    title: 'tutorial.reviewNote',
    steps: [
      // {
      //   title: 'Review your note',
      //   description:
      //     'Your note is generated based on the transcript and any context you have added. You can edit it directly by clicking into it.',
      //   image: NoteReviewNote,
      //   enum: NotePages.REVIEW_NOTE,
      //   order: 1,
      //   total: 4,
      // },
      {
        title: 'tutorial.helpHeidi',
        description: 'tutorial.helpHeidiDesc',
        image: NoteHelpHeidi,
        enum: NotePages.HELP_HEIDI,
        order: 1,
        total: 2,
      },
      {
        title: 'tutorial.askHeidiHelp',
        description: 'tutorial.askHeidiHelpDesc',
        enum: NotePages.ASK_HEIDI_FOR_HELP,
        image: NoteAskHeidi,
        order: 2,
        total: 2,
      },
    ],
  },
  [TutorialSteps.TEMPLATE]: {
    title: 'tutorial.findTemplate',
    steps: [
      {
        title: 'tutorial.goToCommunity',
        description: 'tutorial.goToCommunityDesc',
        image: TemplateGoToTemplateCommunity,
        enum: TemplatePages.GO_TO_TEMPLATE_COMMUNITY,
        order: 1,
        total: 4,
      },
      {
        title: 'tutorial.browseTemplates',
        description: 'tutorial.browseTemplatesDesc',
        image: TemplateBrowseTemplates,
        enum: TemplatePages.BROWSE_TEMPLATES,
        order: 2,
        total: 4,
      },
      {
        title: 'tutorial.addToLibrary',
        description: 'tutorial.addToLibraryDesc',
        image: TemplateAddToYourLibrary,
        enum: TemplatePages.ADD_TO_YOUR_LIBRARY,
        order: 3,
        total: 4,
      },
      {
        title: 'tutorial.navigateBack',
        description: 'tutorial.navigateBackDesc',
        image: TemplateMoveBackToSession,
        enum: TemplatePages.NAVIGATE_BACK_TO_SESSION,
        order: 4,
        total: 4,
      },
      // {
      //   title: 'Set as your default template',
      //   description: 'Click on the ... and then select "Set as default"',
      //   image: TemplateSetAsDefaultTemplate,
      //   enum: TemplatePages.SET_AS_DEFAULT_TEMPLATE,
      //   order: 4,
      //   total: 4,
      // },
    ],
  },
  [TutorialSteps.DOCUMENT]: {
    title: 'tutorial.createDocument',
    steps: [
      {
        title: 'tutorial.createFirstDoc',
        description: 'tutorial.createFirstDocDesc',
        image: DocumentCreate,
        enum: CreateDocumentPages.CREATE_DOCUMENT,
        order: 1,
        total: 2,
      },
      {
        title: 'tutorial.selectDocument',
        description: 'tutorial.selectDocumentDesc',
        image: DocumentSelect,
        enum: CreateDocumentPages.SELECT_DOCUMENT,
        order: 2,
        total: 2,
      },
    ],
  },
  [TutorialSteps.CUSTOM_TEMPLATE]: {
    title: 'tutorial.createCustomTemplate',
    steps: [
      {
        title: 'tutorial.createCustomTemplate',
        description: 'tutorial.createTemplateDesc',
        image: CreateTemplateOpenModal,
        enum: CreateTemplatePages.CREATE_CUSTOM_TEMPLATE,
        order: 1,
        total: 3,
      },
      {
        title: 'tutorial.promptHeidi',
        description: 'tutorial.promptHeidiDesc',
        image: CreateTemplatePromptHeidi,
        enum: CreateTemplatePages.PROMPT_HEIDI_TO_GENERATE_YOUR_PERFECT_TEMPLATE,
        order: 2,
        total: 3,
      },
      {
        title: 'tutorial.saveTemplate',
        description: 'tutorial.saveTemplateDesc',
        image: CreateTemplateSave,
        enum: CreateTemplatePages.SAVE_TEMPLATE,
        order: 3,
        total: 3,
      },
    ],
  },
};

// const mapPendingTasksToOnboardingTasks = (pendingTasks: OnboardingTask) => {
//   switch (pendingTasks) {
//     case OnboardingTask.start_session:
//       return TutorialSteps.SESSION;
//     case OnboardingTask.review_note:
//       return TutorialSteps.NOTE;
//     case OnboardingTask.create_document:
//       return TutorialSteps.DOCUMENT;
//     case OnboardingTask.create_template:
//       return TutorialSteps.TEMPLATE;
//     default:
//       return TutorialSteps.SIGN_UP;
//   }
// }

const mapOnboardingTaskToPendingTasks = (onboardingTask: TutorialSteps) => {
  switch (onboardingTask) {
    case TutorialSteps.SESSION:
      return OnboardingTask.start_session;
    case TutorialSteps.NOTE:
      return OnboardingTask.review_note;
    case TutorialSteps.DOCUMENT:
      return OnboardingTask.create_document;
    case TutorialSteps.TEMPLATE:
      return OnboardingTask.find_template;
    case TutorialSteps.CUSTOM_TEMPLATE:
      return OnboardingTask.create_template;
    default:
      return OnboardingTask.start_session;
  }
};

export function useTutorial() {
  const { t } = useTranslations();
  const [isPopoverOpen, setIsPopoverOpen] = useAtom(panelOpenAtom);
  const [tutorialStep, setTutorialStep] = useAtom(tutorialStepAtom);
  const [recentlyCompletedSteps, setRecentlyCompletedSteps] = useState<TutorialSteps[]>(
    []
  );
  const [isAnimatingFireworks, setIsAnimatingFireworks] = useState(false);
  const { data: user } = useQueryUser((data) => {
    return {
      firstName: data?.first_name,
      pendingTasks: data?.pending_onboarding_tasks,
      currentSubtask: data?.current_onboarding_sub_task,
      onboardingTaskCompletedAt: data?.onboarding_tasks_completed_at,
    };
  });

  const showPercentageChart = useMemo(() => {
    if (!user?.onboardingTaskCompletedAt) {
      return true;
    }

    const completedDate = new Date(user.onboardingTaskCompletedAt);
    const currentDate = new Date();
    const daysDifference = Math.floor(
      (currentDate.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysDifference < 5;
  }, [user?.onboardingTaskCompletedAt]);

  const userName = user?.firstName ?? '';
  const pendingTasks = user?.pendingTasks;
  const currentSubtask = user?.currentSubtask;
  const { mutate: updateUserPendingOnboardingTasks } =
    useMutateUpdateUserOnboardingTask();
  const { mutate: updateUserPendingOnboardingSubTask } =
    useMutateUpdateUserOnboardingSubTask();

  const setPopoverOpen = (open: boolean) => {
    setIsPopoverOpen(open);
    if (open) {
      trackGettingStartedPanelOpened();
    } else {
      trackGettingStartedPanelClosed();
    }
  };

  const upcomingSteps = useMemo(() => {
    return [
      TutorialSteps.SESSION,
      TutorialSteps.NOTE,
      TutorialSteps.TEMPLATE,
      TutorialSteps.DOCUMENT,
      TutorialSteps.CUSTOM_TEMPLATE,
    ].filter((step) => {
      return !pendingTasks?.includes(mapOnboardingTaskToPendingTasks(step));
    });
  }, [pendingTasks]);

  const completedSteps = useMemo(
    () => [...defaultCompletedSteps, ...upcomingSteps],
    [upcomingSteps]
  );

  const hasCompletedAllSteps = completedSteps.length === 7;

  const completeStep = useCallback(
    (step: TutorialSteps) => {
      if (hasCompletedAllSteps) {
        return;
      }

      const task = mapOnboardingTaskToPendingTasks(step);
      void updateUserPendingOnboardingTasks(task);
      setRecentlyCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
    },
    [hasCompletedAllSteps, updateUserPendingOnboardingTasks]
  );

  const clearRecentlyCompletedSteps = () => {
    setRecentlyCompletedSteps([]);
  };

  const completeSteps = useCallback(() => {
    setIsAnimatingFireworks(true);
    trackGettingStartedCompleted();
    fireFireworks();
    setTimeout(() => {
      setIsAnimatingFireworks(false);
    }, 500);
  }, []);

  const steps = useMemo(() => {
    return Object.values(TutorialSteps).map((stepEnum) => ({
      title: t((stepsMapping[stepEnum].title as TranslationKey) || ''),
      completed: completedSteps.includes(stepEnum),
      open: tutorialStep?.step === stepEnum,
      enum: stepEnum,
    }));
  }, [completedSteps, tutorialStep, t]);

  const nextUncompletedStep = steps.find((step) => !step.completed)?.enum;

  const tutorialStepCurrentPage = tutorialStep
    ? stepsMapping[tutorialStep.step]?.steps?.find(
        (step) => step.enum === tutorialStep.page
      )
    : undefined;

  // Translate the current tutorial step page title and description if available
  const translatedCurrentPage = tutorialStepCurrentPage
    ? {
        ...tutorialStepCurrentPage,
        // we need to cast to TranslationKey because t() expects a valid key
        title: t(tutorialStepCurrentPage.title as TranslationKey),
        description: t(tutorialStepCurrentPage.description as TranslationKey),
      }
    : undefined;

  const totalSteps = Object.keys(stepsMapping).length;
  const completedStepCount = steps.filter((step) => step.completed).length;
  const completionPercentage = Math.floor((completedStepCount / totalSteps) * 100);

  const onNextPage = useCallback(() => {
    if (!tutorialStep) {
      return;
    }

    const currentStepPages = stepsMapping[tutorialStep.step]?.steps;
    if (!currentStepPages) {
      return;
    }

    const currentIndex = currentStepPages.findIndex(
      (step) => step.enum === tutorialStep.page
    );

    if (currentIndex >= 0 && currentIndex < currentStepPages.length - 1) {
      const nextPage = currentStepPages[currentIndex + 1];
      if (!nextPage) return;

      const updatedStep = { step: tutorialStep.step, page: nextPage.enum };

      setTutorialStep(updatedStep);

      if (!hasCompletedAllSteps) {
        void updateUserPendingOnboardingSubTask(updatedStep);
      }

      trackGettingStartedPanelNextStep(nextPage.enum);
    } else {
      const nextTutorialStep = Object.values(TutorialSteps).find(
        (stepEnum) =>
          stepEnum !== tutorialStep.step && stepsMapping[stepEnum].steps.length > 0
      );

      if (nextTutorialStep) {
        if (!hasCompletedAllSteps) {
          void updateUserPendingOnboardingSubTask({
            step: null,
            page: null,
          });
        }
        trackGettingStartedPanelNextStep(nextTutorialStep);
        const isOnLastStep = completedSteps.length === 6;
        if (isOnLastStep) {
          setTimeout(() => {
            const currentStep = tutorialStep.step;
            completeStep(currentStep);
            setTutorialStep(undefined);
            completeSteps();
          }, 600);
          return;
        }
        const currentStep = tutorialStep.step;
        completeStep(currentStep);
        setTutorialStep(undefined);
      }
    }
  }, [
    completeStep,
    completeSteps,
    completedSteps.length,
    hasCompletedAllSteps,
    setTutorialStep,
    tutorialStep,
    updateUserPendingOnboardingSubTask,
  ]);

  const onBackPage = useCallback(() => {
    if (!tutorialStep) {
      return;
    }

    const currentStepPages = stepsMapping[tutorialStep.step]?.steps;
    if (!currentStepPages) {
      return;
    }

    const currentIndex = currentStepPages.findIndex(
      (step) => step.enum === tutorialStep.page
    );

    if (currentIndex > 0) {
      // Go back to the previous page
      const prevPage = currentStepPages[currentIndex - 1];
      if (!prevPage) return;

      const updatedStep = { step: tutorialStep.step, page: prevPage.enum };

      setTutorialStep(updatedStep);

      if (!hasCompletedAllSteps) {
        void updateUserPendingOnboardingSubTask(updatedStep);
      }
    } else {
      setTutorialStep(undefined);
    }
  }, [
    hasCompletedAllSteps,
    setTutorialStep,
    tutorialStep,
    updateUserPendingOnboardingSubTask,
  ]);

  const sessionId = useCurrentSessionId();
  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        session_id: data.session_id,
        consult_note: data.consult_note,
      };
    },
  });

  const router = useRouter();
  // if user on /community
  const isOnCommunityPage = router.asPath.includes('/templates');

  useEffect(() => {
    if (
      session?.session_id &&
      session.consult_note.status === ConsultNoteStatus.CREATED
    ) {
      // if on empty session, skip a step as no need to go through the session creation
      if (
        (tutorialStep?.step === TutorialSteps.SESSION &&
          tutorialStepCurrentPage?.enum === SessionPages.START_SESSION) ||
        !tutorialStep
      ) {
        onNextPage();
      }
    }
  }, [
    onNextPage,
    session?.consult_note.status,
    session?.session_id,
    tutorialStep,
    tutorialStepCurrentPage?.enum,
  ]);

  useEffect(() => {
    if (isOnCommunityPage) {
      if (
        tutorialStep?.step === TutorialSteps.TEMPLATE &&
        tutorialStepCurrentPage?.enum === TemplatePages.GO_TO_TEMPLATE_COMMUNITY
      ) {
        onNextPage();
      }
    }
  }, [tutorialStep, tutorialStepCurrentPage, isOnCommunityPage, onNextPage]);

  useEffect(() => {
    if (!tutorialStep && currentSubtask && currentSubtask.step && currentSubtask.page) {
      // @ts-expect-error: Type mismatch between currentSubtask and tutorialStep
      setTutorialStep((prev) => {
        if (prev?.step === currentSubtask.step && prev?.page === currentSubtask.page) {
          return prev;
        }
        return { step: currentSubtask.step, page: currentSubtask.page };
      });
    }
  }, [currentSubtask, setTutorialStep, tutorialStep]);

  return {
    panelOpen: isPopoverOpen,
    setPanelOpen: setPopoverOpen,
    tutorialStep,
    setTutorialStep,
    tutorialStepCurrentPage: translatedCurrentPage,
    onNextPage,
    onBackPage,
    steps,
    nextUncompletedStep,
    isAnimatingFireworks,
    hasCompletedAllSteps,
    recentlyCompletedSteps,
    clearRecentlyCompletedSteps,
    completionPercentage,
    userName,
    showPercentageChart,
  };
}
